import React, { useState } from "react";
import { toast } from "sonner";
import { Broom, Warning, Check, ShieldWarning } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";

export default function AdminResetBalances() {
  const [step, setStep] = useState(0); // 0 idle, 1 confirming
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canConfirm = typed.trim().toUpperCase() === "ZERAR";

  const doReset = async () => {
    if (!canConfirm || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/balances/reset");
      setLastResult(data);
      toast.success(`Saldos zerados de ${data.affected} contas`);
      setStep(0);
      setTyped("");
    } catch (e) {
      toast.error(errMsg(e, "Erro ao zerar"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fadeUp max-w-3xl" data-testid="admin-reset-page">
      <div className="label-caps mb-2 flex items-center gap-1"><Broom size={12} /> Manutenção do banco</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-4">
        Zerar saldos
      </h1>
      <p className="text-white/60 text-sm mb-8 max-w-2xl">
        Reseta <b>saldo</b>, <b>comissão acumulada</b>, <b>valor a receber</b> e <b>reserva</b> de <b>todos</b> os usuários da plataforma (exceto o admin). Use apenas para limpar dados de teste antes de ir para produção.
      </p>

      <div className="card-flat p-6 md:p-8 border-l-2 border-l-[var(--danger)]" data-testid="reset-card">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-sm bg-[var(--danger)]/15 flex items-center justify-center shrink-0">
            <ShieldWarning size={24} weight="fill" color="var(--danger)" />
          </div>
          <div>
            <div className="font-display font-bold text-xl text-[var(--danger)]">
              Ação irreversível
            </div>
            <div className="text-sm text-white/70 mt-1">
              Nenhum registro de transação é apagado. Apenas os campos numéricos das contas voltam a <b>zero</b>. Histórico de vendas, saques e afiliados permanece.
            </div>
          </div>
        </div>

        <ul className="text-sm text-white/60 space-y-1.5 mb-6 pl-1">
          <li>· <b>balance</b> → R$ 0,00</li>
          <li>· <b>commission_earned</b> → R$ 0,00</li>
          <li>· <b>pending_income</b> → R$ 0,00</li>
          <li>· <b>reserve</b> → R$ 0,00</li>
        </ul>

        {step === 0 ? (
          <button
            onClick={() => setStep(1)}
            data-testid="reset-open"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm bg-[var(--danger)] text-white font-bold text-sm hover:brightness-110 transition"
          >
            <Broom size={16} weight="bold" /> Zerar todos os saldos agora
          </button>
        ) : (
          <div className="border border-[var(--danger)]/40 bg-[var(--danger)]/[0.05] rounded-sm p-5">
            <div className="flex items-start gap-2 mb-4">
              <Warning size={18} weight="fill" color="var(--danger)" className="mt-0.5 shrink-0" />
              <div className="text-sm">
                <b className="text-[var(--danger)]">Confirmação de segurança.</b> Para prosseguir, digite <code className="text-[var(--accent)] font-bold px-1">ZERAR</code> abaixo (em maiúsculas).
              </div>
            </div>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Digite ZERAR"
              data-testid="reset-confirm-input"
              autoFocus
              className="w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--danger)] outline-none font-mono text-center tracking-widest"
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => { setStep(0); setTyped(""); }}
                disabled={loading}
                data-testid="reset-cancel"
                className="text-xs px-4 py-2 rounded-sm border border-white/10 hover:bg-white/5"
              >
                Cancelar
              </button>
              <button
                onClick={doReset}
                disabled={!canConfirm || loading}
                data-testid="reset-confirm"
                className="text-xs px-4 py-2 rounded-sm bg-[var(--danger)] text-white font-bold flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Zerando...
                  </>
                ) : (
                  <>
                    <Check size={12} weight="bold" /> Zerar {typed.trim().toUpperCase() === "ZERAR" ? "agora" : "(digite ZERAR)"}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {lastResult && (
          <div className="mt-6 p-4 rounded-sm border border-[var(--success)]/40 bg-[var(--success)]/[0.05] text-sm" data-testid="reset-result">
            <div className="flex items-center gap-2 text-[var(--success)] font-bold mb-1">
              <Check size={14} weight="bold" /> Saldos zerados
            </div>
            <div className="text-white/70 text-xs">
              {lastResult.affected} conta{lastResult.affected === 1 ? "" : "s"} atualizada{lastResult.affected === 1 ? "" : "s"} com sucesso.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
